import { Box, List, SearchField, StatusLabel, Text } from '@factorialco/deprecated-design-system'
import { DataSchemaItemFragment } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import { MappedFields } from '../../types'

interface FieldListItemProps {
  field: DataSchemaItemFragment
  isMapped: boolean
}

const FieldListItem = ({ field, isMapped }: FieldListItemProps) => {
  const type = isMapped ? 'positive' : 'neutral'
  const description = isMapped
    ? i18n.t('importer_process.map_columns_step.sidepanel.matched')
    : i18n.t('importer_process.map_columns_step.sidepanel.pending')

  return (
    <List.Item key={field.fieldName}>
      <List.Title>{field.label}</List.Title>
      <List.Description>
        <StatusLabel type={type}>{description}</StatusLabel>
      </List.Description>
    </List.Item>
  )
}

export interface MapColumnsSidepanelProps {
  fields: DataSchemaItemFragment[]
  mappedFields: MappedFields[]
  onClose: () => void
}

const MapColumnsSidepanel = ({ fields, mappedFields, onClose }: MapColumnsSidepanelProps) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const requiredFields = fields.filter((field) => field.required)
  const requiredMappedFields = mappedFields.filter((field) =>
    requiredFields.some((requiredField) => field.fieldName === requiredField.fieldName)
  )

  const nonRequiredFields = fields.filter((field) => !field.required)
  const nonRequiredMappedFields = mappedFields.filter(
    (field) =>
      !requiredMappedFields.some((requiredField) => requiredField.fieldName === field.fieldName)
  )

  const filteredFields = () => {
    if (!debouncedSearch) return fields

    return fields.filter((field) => matchText(debouncedSearch, field.label))
  }

  const requiredFilteredFields = filteredFields().filter((field) => field.required)
  const nonRequiredFilteredFields = filteredFields().filter((field) => !field.required)

  const isFieldMapped = (field: DataSchemaItemFragment) => {
    return mappedFields.some((mappedField) => mappedField.fieldName === field.fieldName)
  }

  return (
    <Sidepanel
      onClose={onClose}
      open
      title={i18n.t('importer_process.map_columns_step.sidepanel.title')}
      width="wide"
    >
      <Box gap="s32">
        <SearchField
          placeholder={i18n.t('importer_process.map_columns_step.sidepanel.search.placeholder')}
          onSearch={onSearch}
          search={search}
        />
        {!!requiredFilteredFields.length && (
          <Box gap="s24">
            <Box gap="s8">
              <Text.Header3>{`${i18n.t('common.required')} (${requiredMappedFields.length}/${
                requiredFields.length
              })`}</Text.Header3>
              <Text.Normal>
                {i18n.t('importer_process.map_columns_step.sidepanel.required_fields.description')}
              </Text.Normal>
            </Box>
            <List>
              {requiredFilteredFields.map((field) => (
                <FieldListItem
                  key={field.fieldName}
                  field={field}
                  isMapped={isFieldMapped(field)}
                />
              ))}
            </List>
          </Box>
        )}
        {!!nonRequiredFilteredFields.length && (
          <Box gap="s24">
            <Box gap="s8">
              <Text.Header3>{`${i18n.t('common.others')} (${nonRequiredMappedFields.length}/${
                nonRequiredFields.length
              })`}</Text.Header3>
              <Text.Normal>
                {i18n.t(
                  'importer_process.map_columns_step.sidepanel.non_required_fields.description'
                )}
              </Text.Normal>
            </Box>
            <List>
              {nonRequiredFilteredFields.map((field) => (
                <FieldListItem
                  key={field.fieldName}
                  field={field}
                  isMapped={isFieldMapped(field)}
                />
              ))}
            </List>
          </Box>
        )}
      </Box>
    </Sidepanel>
  )
}

export default MapColumnsSidepanel
